import { parseNaturalInput } from '@/_pure/naturalInput';
import { BOOLEAN_OPERATIONS } from '@/adapters/primitive/_data/booleanOperations';
import { powersForValue } from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import { toBooleanOperandOffset } from '@/adapters/primitive/_pure/derivation/boolean/geometry';
import type { BooleanAlgebraOperation } from '@/adapters/primitive/_contract/boolean/operation';
import type { OperandSchedulingMode } from '@/adapters/primitive/_contract/natural/arithmetic';
import type { NaturalReceiverLocation } from '@/adapters/primitive/_contract/natural/location';
import type { NaturalBandState } from '@/adapters/primitive/_contract/natural/machine';
import type {
    Config,
    PrimitivePresentationOperation,
    PrimitiveWheelRole,
    PrimitiveWheelState,
} from '@/adapters/primitive/_contract/config';
import type { PrimitiveOperationInputDraft } from '@/adapters/primitive/_contract/operation';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

/** Default depth order front-to-back: inputs in front (+z), outputs in back (-z). */
const DEFAULT_WHEEL_DEPTH_BY_ROLE: Record<PrimitiveWheelRole, number> = {
    // Addition
    'addend-two': 1,
    'addend-one': 0,
    sum: -1,
    // Subtraction
    subtrahend: 1,
    minuend: 0,
    difference: -1,
    // Multiplication
    multiplier: 2,
    'remaining-repetitions': 1,
    multiplicand: 0,
    product: -1,
    // Division (4-depth axial stack; NaturalAdd's three stations, with Quotient receding into -z)
    dividend: 1,
    divisor: 0,
    remainder: -1,
    quotient: -2,
    // Identity
    'identity-input': 0.5,
    'identity-output': -0.5,
    'boolean-input-one': 1,
    'boolean-input-two': 1,
    'boolean-output': -1,
    // NaturalLessThan
    'less-than-source': 1,
    'less-than-decision': -1,
    'less-than-target': 0,
};

/**
 * Which operand sits in front differs by scheduling mode so travelling bands never cross undimmed bands
 * on another wheel. Depth is therefore a property of the trace, not of the ambient config.
 */
function toWheelDepth(operandSchedulingMode: OperandSchedulingMode, role: PrimitiveWheelRole): number {
    if (operandSchedulingMode === 'grouped') {
        switch (role) {
            case 'addend-one':
            case 'minuend':
                return 1;
            case 'addend-two':
            case 'subtrahend':
                return 0;
        }
    }
    return DEFAULT_WHEEL_DEPTH_BY_ROLE[role];
}

export type PrimitiveWheelLayout = {
    depthByRole: Record<PrimitiveWheelRole, number>;
    wheelGap: number;
};

export function toWheelLayout(args: {
    config: Config;
    operandSchedulingMode: OperandSchedulingMode;
}): PrimitiveWheelLayout {
    const roles = Object.keys(DEFAULT_WHEEL_DEPTH_BY_ROLE) as PrimitiveWheelRole[];
    return {
        depthByRole: Object.fromEntries(
            roles.map((role) => [role, toWheelDepth(args.operandSchedulingMode, role)]),
        ) as Record<PrimitiveWheelRole, number>,
        wheelGap: args.config.base.display.wheel.wheelGap,
    };
}

export function toWheelPosition(args: {
    layout: PrimitiveWheelLayout;
    role: PrimitiveWheelRole;
}): [number, number, number] {
    return [0, 0, args.layout.depthByRole[args.role] * args.layout.wheelGap];
}

export function toBooleanAlgebraWheelPosition(args: {
    config: Config;
    layout: PrimitiveWheelLayout;
    role: 'boolean-input-one' | 'boolean-input-two' | 'boolean-output';
    operation: BooleanAlgebraOperation;
}): [number, number, number] {
    const position = toWheelPosition({ layout: args.layout, role: args.role });
    if (args.operation === 'logical-not' || args.role === 'boolean-output') return position;
    const height = BOOLEAN_OPERATIONS[args.operation].paths.length === 1 ? 0 : args.config.boolean.display.layout.branchHeight;
    const offset = toBooleanOperandOffset(args.config, height);
    const outputDepth = toWheelPosition({ layout: args.layout, role: 'boolean-output' })[2];
    return [args.role === 'boolean-input-one' ? -offset.lateral : offset.lateral, 0, outputDepth + offset.depth];
}

export function toDepthOrderedWheels(wheels: readonly PrimitiveWheelState[]): PrimitiveWheelState[] {
    return [...wheels].sort((a, b) => b.position[2] - a.position[2]);
}

/** Inset keeps the hub line off the 10^0 face edge, where a coplanar line and face z-fight. */
export function toPrimitiveHubRingRadius(config: Config): number {
    return config.base.display.wheel.innerRadius * 0.96;
}

/** Placeholders of either resource kind render as a disc so they can collapse onto the hub ring. */
export function isPrimitiveDiscWheel(
    wheel: PrimitiveWheelState,
): wheel is Exclude<PrimitiveWheelState, { value: number }> {
    return wheel.valueKind === 'boolean'
        || wheel.valueKind === 'comparison-helper'
        || wheel.valueKind === 'placeholder';
}

export function toPrimitiveWheelOpacity(wheel: PrimitiveWheelState): number {
    return wheel.wheelOpacity ?? 1;
}

export function toPrimitiveBandOpacity(wheel: PrimitiveWheelState, power: number): number {
    return toPrimitiveWheelOpacity(wheel) * (wheel.bandOpacityByPower?.[power] ?? 1);
}

/** Unless explicitly directed, the hub dims with the wheel once every band has. */
export function toPrimitiveHubOpacity(wheel: PrimitiveWheelState): number {
    if (wheel.hubOpacity !== undefined) return toPrimitiveWheelOpacity(wheel) * wheel.hubOpacity;
    if (wheel.powers.length === 0) return toPrimitiveWheelOpacity(wheel);
    return Math.max(...wheel.powers.map((power) => toPrimitiveBandOpacity(wheel, power)));
}

/** The camera frames a helper's settled footprint, so its entrance growth never shifts a view or anchor. */
export function toPrimitiveCameraSteps(steps: readonly PrimitiveStepState[]): PrimitiveStepState[] {
    return steps.map((step) => step.keyframes.some((keyframe) => keyframe.wheels.some((wheel) => wheel.entranceGrowth !== undefined))
        ? {
            ...step,
            keyframes: step.keyframes.map((keyframe) => ({
                ...keyframe,
                wheels: keyframe.wheels.map((wheel) => {
                    if (wheel.entranceGrowth === undefined) return wheel;
                    const settled = { ...wheel };
                    delete settled.entranceGrowth;
                    return settled;
                }),
            })),
        }
        : step);
}

export function dimStableWheelRoles(args: {
    config: Config;
    wheels: readonly PrimitiveWheelState[];
    roles: readonly PrimitiveWheelRole[];
}): PrimitiveWheelState[] {
    const roles = new Set(args.roles);
    return args.wheels.map((wheel) => (
        wheel.variant !== undefined || !roles.has(wheel.role)
            ? wheel
            : {
                ...wheel,
                bandOpacityByPower: Object.fromEntries(
                    wheel.powers.map((power) => [power, args.config.natural.display.band.spentOpacity]),
                ),
            }
    ));
}

export function shiftWheelPowers(wheel: PrimitiveWheelState, powerOffset: number): PrimitiveWheelState {
    if (powerOffset === 0) return wheel;
    const shiftedPowers = wheel.powers.map((p) => p + powerOffset);
    const shiftedCountedThroughDigit: Partial<Record<number, number>> = {};
    if (wheel.countedThroughDigitByPower) {
        for (const [p, d] of Object.entries(wheel.countedThroughDigitByPower)) {
            shiftedCountedThroughDigit[Number(p) + powerOffset] = d;
        }
    }
    return {
        ...wheel,
        powers: shiftedPowers,
        ...(wheel.countedThroughDigitByPower ? { countedThroughDigitByPower: shiftedCountedThroughDigit } : {}),
    };
}

export function buildOperandWheel(args: {
    layout: PrimitiveWheelLayout;
    role: PrimitiveWheelRole;
    value: number;
    wheelOpacity?: number;
    bandOpacityByPower?: Partial<Record<number, number>>;
}): PrimitiveWheelState {
    return {
        role: args.role,
        value: args.value,
        powers: powersForValue(args.value),
        position: toWheelPosition({ layout: args.layout, role: args.role }),
        ...(args.wheelOpacity === undefined ? {} : { wheelOpacity: args.wheelOpacity }),
        ...(args.bandOpacityByPower === undefined ? {} : { bandOpacityByPower: args.bandOpacityByPower }),
    };
}

export function buildFlightWheel(args: {
    role: PrimitiveWheelRole;
    variant?: 'flight' | 'radix-band' | 'marker' | 'ante';
    value: number;
    power: number;
    position: [number, number, number];
    wheelOpacity?: number;
    rotation?: number;
    opacity?: number;
    countedDigits?: readonly number[];
    overhangDigits?: readonly number[];
    mark?: 'carry' | 'deficit';
    receiverLocation?: NaturalReceiverLocation;
}): PrimitiveWheelState {
    return {
        role: args.role,
        variant: args.variant ?? 'flight',
        value: args.value,
        powers: [args.power],
        position: args.position,
        ...(args.wheelOpacity === undefined ? {} : { wheelOpacity: args.wheelOpacity }),
        ...(args.rotation === undefined ? {} : { rotationByPower: { [args.power]: args.rotation } }),
        ...(args.opacity === undefined ? {} : { bandOpacityByPower: { [args.power]: args.opacity } }),
        ...(args.countedDigits === undefined ? {} : { countedDigitsByPower: { [args.power]: [...args.countedDigits] } }),
        ...(args.overhangDigits === undefined ? {} : { overhangDigitsByPower: { [args.power]: [...args.overhangDigits] } }),
        ...(args.mark === undefined ? {} : { markByPower: { [args.power]: args.mark } }),
        ...(args.receiverLocation === undefined ? {} : { receiverLocation: args.receiverLocation }),
    };
}

export function buildWholeFlightWheel(args: {
    role: PrimitiveWheelRole;
    value: number;
    bands: readonly NaturalBandState[];
    position: [number, number, number];
    opacity?: number;
}): PrimitiveWheelState {
    return {
        role: args.role,
        variant: 'flight',
        value: args.value,
        powers: args.bands.map((band) => band.power),
        position: args.position,
        countedDigitsByPower: Object.fromEntries(
            args.bands.map((band) => [band.power, [...band.countedSlots]]),
        ),
        overhangDigitsByPower: Object.fromEntries(
            args.bands.map((band) => [band.power, [...band.overhangSlots]]),
        ),
        ...(args.opacity === undefined
            ? {}
            : {
                bandOpacityByPower: Object.fromEntries(
                    args.bands.map((band) => [band.power, args.opacity]),
                ),
            }),
    };
}

export function buildResultWheel(args: {
    layout: PrimitiveWheelLayout;
    role: PrimitiveWheelRole;
    powers: readonly number[];
    countedThroughDigitByPower: Partial<Record<number, number>>;
    countedDigitsByPower?: Partial<Record<number, readonly number[]>>;
    wheelOpacity?: number;
    markByPower?: Partial<Record<number, 'carry' | 'deficit'>>;
}): PrimitiveWheelState {
    return {
        role: args.role,
        value: 0,
        powers: args.powers,
        position: toWheelPosition({ layout: args.layout, role: args.role }),
        countedThroughDigitByPower: args.countedThroughDigitByPower,
        ...(args.countedDigitsByPower === undefined ? {} : { countedDigitsByPower: args.countedDigitsByPower }),
        ...(args.wheelOpacity === undefined ? {} : { wheelOpacity: args.wheelOpacity }),
        ...(args.markByPower === undefined ? {} : { markByPower: args.markByPower }),
    };
}

export function buildNaturalPlaceholderWheel(args: {
    layout: PrimitiveWheelLayout;
    role: PrimitiveWheelRole;
}): PrimitiveWheelState {
    return {
        valueKind: 'placeholder',
        resourceKind: 'natural',
        role: args.role,
        powers: [0],
        position: toWheelPosition({ layout: args.layout, role: args.role }),
    };
}

export function buildBooleanPlaceholderWheel(args: {
    layout: PrimitiveWheelLayout;
    role: PrimitiveWheelRole;
}): PrimitiveWheelState {
    return {
        valueKind: 'placeholder',
        resourceKind: 'boolean',
        role: args.role,
        powers: [0],
        position: toWheelPosition({ layout: args.layout, role: args.role }),
    };
}

export function buildPrimitiveOperationPlaceholderWheels(args: {
    config: Config;
    operation: PrimitivePresentationOperation;
}): PrimitiveWheelState[] {
    // Pre-run wheels stand in for the run the config is about to perform, so its scheduling mode applies.
    const layout = toWheelLayout({
        config: args.config,
        operandSchedulingMode: args.config.natural.operandSchedulingMode,
    });
    const natural = (role: PrimitiveWheelRole) => buildNaturalPlaceholderWheel({ layout, role });
    const boolean = (role: PrimitiveWheelRole) => buildBooleanPlaceholderWheel({ layout, role });
    const algebra = (role: 'boolean-input-one' | 'boolean-input-two' | 'boolean-output', operation: BooleanAlgebraOperation) => ({
        ...boolean(role),
        position: toBooleanAlgebraWheelPosition({ config: args.config, layout, role, operation }),
    });

    switch (args.operation) {
        case 'addition':
            return [natural('addend-one'), natural('addend-two'), natural('sum')];
        case 'subtraction':
            return [natural('minuend'), natural('subtrahend'), natural('difference')];
        case 'multiplication':
            return [natural('multiplier'), natural('multiplicand'), natural('product')];
        case 'division':
            return [natural('dividend'), natural('divisor'), natural('remainder'), natural('quotient')];
        case 'natural-identity':
            return [natural('identity-input'), natural('identity-output')];
        case 'boolean-identity':
            return [boolean('identity-input'), boolean('identity-output')];
        case 'logical-not':
            return [algebra('boolean-input-one', args.operation), algebra('boolean-output', args.operation)];
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            return [algebra('boolean-input-one', args.operation), algebra('boolean-input-two', args.operation), algebra('boolean-output', args.operation)];
        case 'natural-less-than':
            return [natural('less-than-source'), boolean('less-than-decision'), natural('less-than-target')];
    }
}

export function buildPrimitiveOperationDraftWheels(args: {
    config: Config;
    draft: PrimitiveOperationInputDraft;
}): PrimitiveWheelState[] {
    const layout = toWheelLayout({
        config: args.config,
        operandSchedulingMode: args.config.natural.operandSchedulingMode,
    });
    const natural = (role: PrimitiveWheelRole) => buildNaturalPlaceholderWheel({ layout, role });
    const boolean = (role: PrimitiveWheelRole) => buildBooleanPlaceholderWheel({ layout, role });
    const maxDigits = args.draft.operation === 'multiplication' || args.draft.operation === 'division' ? 2 : 3;
    const naturalInput = (role: PrimitiveWheelRole, value: string) => {
        const parsed = parseNaturalInput(value, { maxDigits });
        return parsed === null ? natural(role) : buildOperandWheel({ layout, role, value: parsed });
    };
    const booleanInput = (role: PrimitiveWheelRole, value: '' | 'true' | 'false'): PrimitiveWheelState => {
        if (value === '') return boolean(role);
        return {
            valueKind: 'boolean',
            role,
            value: value === 'true',
            powers: [0],
            position: toWheelPosition({ layout, role }),
        };
    };
    const algebraInput = (role: 'boolean-input-one' | 'boolean-input-two', value: '' | 'true' | 'false', operation: BooleanAlgebraOperation): PrimitiveWheelState => ({
        ...booleanInput(role, value),
        position: toBooleanAlgebraWheelPosition({ config: args.config, layout, role, operation }),
    });
    const algebraOutput = (operation: BooleanAlgebraOperation): PrimitiveWheelState => ({
        ...boolean('boolean-output'),
        position: toBooleanAlgebraWheelPosition({ config: args.config, layout, role: 'boolean-output', operation }),
    });

    switch (args.draft.operation) {
        case 'addition':
            return [
                naturalInput('addend-one', args.draft.operandOneText),
                naturalInput('addend-two', args.draft.operandTwoText),
                natural('sum'),
            ];
        case 'subtraction':
            return [
                naturalInput('minuend', args.draft.operandOneText),
                naturalInput('subtrahend', args.draft.operandTwoText),
                natural('difference'),
            ];
        case 'multiplication':
            return [
                { ...naturalInput('multiplier', args.draft.operandOneText),
                    position: [0, 0, layout.wheelGap] },
                naturalInput('multiplicand', args.draft.operandTwoText),
                natural('product'),
            ];
        case 'division':
            return [
                naturalInput('dividend', args.draft.operandOneText),
                naturalInput('divisor', args.draft.operandTwoText),
                natural('remainder'),
                natural('quotient'),
            ];
        case 'natural-identity':
            return [naturalInput('identity-input', args.draft.inputText), natural('identity-output')];
        case 'boolean-identity':
            return [booleanInput('identity-input', args.draft.inputText), boolean('identity-output')];
        case 'logical-not':
            return [algebraInput('boolean-input-one', args.draft.inputText, args.draft.operation), algebraOutput(args.draft.operation)];
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            return [
                algebraInput('boolean-input-one', args.draft.operandOneText, args.draft.operation),
                algebraInput('boolean-input-two', args.draft.operandTwoText, args.draft.operation),
                algebraOutput(args.draft.operation),
            ];
        case 'natural-less-than':
            return [
                naturalInput('less-than-source', args.draft.sourceText),
                boolean('less-than-decision'),
                naturalInput('less-than-target', args.draft.targetText),
            ];
    }
}
